/**
 * Onde a extensão NUNCA grava: bancos e pagamentos, e-mail pessoal, RH e gestores de
 * palavras-passe. "dominio.com" cobre o domínio e os subdomínios; "dominio.com/caminho"
 * restringe a esse prefixo. Compara pelo hostname real ("banco.com.evil.com" não coincide).
 * (Vem do ManualMaker, mmpolicy.js.)
 *
 * A app de Mac (ADR-G25) junta regras para apps: "app:<bundle id>". Não são endereços, por
 * isso a comparação dos sites (e as versões antigas da extensão) passam-lhes ao lado.
 */
import type { Locale } from '@nolens/i18n';
export const DEFAULT_BLOCKLIST = [
  // Bancos e pagamentos (PT e BR)
  'cgd.pt',
  'millenniumbcp.pt',
  'novobanco.pt',
  'santander.pt',
  'bancobpi.pt',
  'bancoctt.pt',
  'activobank.pt',
  'creditoagricola.pt',
  'montepio.pt',
  'moey.pt',
  'revolut.com',
  'wise.com',
  'itau.com.br',
  'bradesco.com.br',
  'bb.com.br',
  'santander.com.br',
  'caixa.gov.br',
  'nubank.com.br',
  'inter.co',
  'c6bank.com.br',
  'btgpactual.com',
  'paypal.com',
  'mercadopago.com.br',
  'picpay.com',
  // Finanças e segurança social
  'portaldasfinancas.gov.pt',
  'seg-social.pt',
  'gov.br/receitafederal',
  // E-mail pessoal
  'mail.google.com',
  'outlook.live.com',
  'hotmail.com',
  'live.com',
  'mail.yahoo.com',
  'proton.me',
  'icloud.com',
  // RH e salários
  'myworkday.com',
  'workday.com',
  'successfactors.com',
  'successfactors.eu',
  'adp.com',
  'adp.com.br',
  // Gestores de palavras-passe
  '1password.com',
  'lastpass.com',
  'bitwarden.com',
  'dashlane.com',
];

export interface ListEntry {
  host: string;
  path: string;
}

/** "https://*.Banco.pt/conta/", "banco.pt:443"… → { host, path } ou null. */
export function parseEntry(raw: string): ListEntry | null {
  let s = String(raw || '')
    .trim()
    .toLowerCase();
  if (!s || s.startsWith('#') || s.startsWith(APP_PREFIX)) return null;
  s = s.replace(/^[a-z][a-z0-9+.-]*:\/\//, '').replace(/^\*\./, '');
  const slash = s.indexOf('/');
  let host = slash === -1 ? s : s.slice(0, slash);
  const path = slash === -1 ? '' : s.slice(slash).replace(/\/+$/, '');
  host = host.replace(/:\d+$/, '').replace(/\.$/, '');
  if (!host || /[\s*]/.test(host)) return null;
  try {
    host = new URL('http://' + host).hostname;
  } catch {
    return null;
  }
  return { host, path };
}

/** Primeira entrada da lista que cobre o URL, ou null. */
export function matchList(list: string[], url: string): string | null {
  let u: URL;
  try {
    u = new URL(url);
  } catch {
    return null;
  }
  const host = u.hostname.replace(/\.$/, '');
  const path = u.pathname.toLowerCase();
  for (const raw of list) {
    const e = parseEntry(raw);
    if (!e) continue;
    if (host !== e.host && !host.endsWith('.' + e.host)) continue;
    if (!e.path || path.startsWith(e.path)) return raw;
  }
  return null;
}

/** Só se grava em páginas web (http/https). */
export function isRecordable(url: string | undefined): boolean {
  return !!url && /^https?:\/\//.test(url);
}

/**
 * Regra de um site (gerida pelo proprietário no portal de administração):
 * - protected: não se grava por omissão; cada pessoa pode desbloquear, com confirmação;
 * - locked: nunca se grava; só o proprietário pode desbloquear.
 */
export type SiteMode = 'protected' | 'locked';

export interface SiteRule {
  pattern: string;
  mode: SiteMode;
}

/** Gestores de palavras-passe: bloqueados (sem desbloqueio para as pessoas). */
const LOCKED_BY_DEFAULT = new Set([
  '1password.com',
  'lastpass.com',
  'bitwarden.com',
  'dashlane.com',
]);

/** Prefixo das regras para apps do Mac: "app:com.1password.1password". */
export const APP_PREFIX = 'app:';

/** Padrão de uma app do Mac a partir do bundle id (sem maiúsculas: o macOS não as distingue). */
export const appPattern = (bundleId: string) => APP_PREFIX + bundleId.trim().toLowerCase();

/** Bundle id de uma regra de app, ou null se a regra for de um site. */
export function appOfPattern(pattern: string): string | null {
  const p = pattern.trim().toLowerCase();
  if (!p.startsWith(APP_PREFIX)) return null;
  const id = p.slice(APP_PREFIX.length);
  return /^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(id) ? id : null;
}

/** Apps do Mac onde a app NUNCA grava: gestores de palavras-passe (bloqueados) e o Mail. */
export const DEFAULT_APP_RULES: SiteRule[] = [
  { pattern: 'app:com.1password.1password', mode: 'locked' },
  { pattern: 'app:com.agilebits.onepassword7', mode: 'locked' },
  { pattern: 'app:com.bitwarden.desktop', mode: 'locked' },
  { pattern: 'app:org.keepassxc.keepassxc', mode: 'locked' },
  { pattern: 'app:com.apple.keychainaccess', mode: 'locked' },
  { pattern: 'app:com.apple.passwords', mode: 'locked' },
  { pattern: 'app:com.apple.mail', mode: 'protected' },
];

/** Regras por omissão (as mesmas que a migração cria; servem enquanto a lista da conta não chega). */
export const DEFAULT_RULES: SiteRule[] = [
  ...DEFAULT_BLOCKLIST.map((pattern) => ({
    pattern,
    mode: (LOCKED_BY_DEFAULT.has(pattern) ? 'locked' : 'protected') as SiteMode,
  })),
  ...DEFAULT_APP_RULES,
];

export interface SitePolicy {
  rules: SiteRule[];
  /** Regras que esta pessoa desbloqueou (padrões). */
  unlocked: string[];
  /** Proprietário da aplicação (confirmado pela API): pode desbloquear também as bloqueadas. */
  admin: boolean;
}

export type SiteCheck =
  | { blocked: false }
  | { blocked: true; reason: string; rule: SiteRule | null; canUnlock: boolean };

/** Motivo de não gravar, na língua da interface. */
const REASONS: Record<
  Locale,
  { internal: string; locked: string; protected: string; appLocked: string; appProtected: string }
> = {
  en: {
    internal: 'internal browser page',
    locked: 'locked site',
    protected: 'protected site',
    appLocked: 'locked app',
    appProtected: 'protected app',
  },
  'pt-PT': {
    internal: 'página interna do browser',
    locked: 'site bloqueado',
    protected: 'site protegido',
    appLocked: 'app bloqueada',
    appProtected: 'app protegida',
  },
};

/** Pode gravar-se neste endereço? Se não, porquê e se pode ser desbloqueado. */
export function checkSite(
  url: string,
  policy?: Partial<SitePolicy>,
  locale: Locale = 'pt-PT',
): SiteCheck {
  const why = REASONS[locale] ?? REASONS.en;
  if (!isRecordable(url))
    return { blocked: true, reason: why.internal, rule: null, canUnlock: false };
  const rules = policy?.rules ?? DEFAULT_RULES;
  const unlocked = new Set(policy?.unlocked ?? []);
  const hit = matchList(
    rules.map((r) => r.pattern),
    url,
  );
  if (!hit) return { blocked: false };
  const rule = rules.find((r) => r.pattern === hit)!;
  // Um desbloqueio só vale para as regras que o permitem (ou para o proprietário)
  if (unlocked.has(rule.pattern) && (rule.mode === 'protected' || policy?.admin))
    return { blocked: false };
  return {
    blocked: true,
    reason: (rule.mode === 'locked' ? why.locked : why.protected) + ' (' + rule.pattern + ')',
    rule,
    canUnlock: rule.mode === 'protected' || !!policy?.admin,
  };
}

/**
 * Pode gravar-se nesta app do Mac? Como checkSite, com as regras "app:<bundle id>". Os sites
 * abertos num browser passam também por checkSite.
 */
export function checkApp(
  bundleId: string,
  policy?: Partial<SitePolicy>,
  locale: Locale = 'pt-PT',
): SiteCheck {
  const why = REASONS[locale] ?? REASONS.en;
  const id = bundleId.trim().toLowerCase();
  const rules = policy?.rules ?? DEFAULT_RULES;
  const rule = id ? rules.find((r) => appOfPattern(r.pattern) === id) : undefined;
  if (!rule) return { blocked: false };
  const unlocked = new Set(policy?.unlocked ?? []);
  if (unlocked.has(rule.pattern) && (rule.mode === 'protected' || policy?.admin))
    return { blocked: false };
  return {
    blocked: true,
    reason: (rule.mode === 'locked' ? why.appLocked : why.appProtected) + ' (' + id + ')',
    rule,
    canUnlock: rule.mode === 'protected' || !!policy?.admin,
  };
}

/** Motivo para não gravar este endereço, ou null. */
export function blockReason(
  url: string,
  policyOrExtra: Partial<SitePolicy> | string[] = [],
): string | null {
  const policy = Array.isArray(policyOrExtra)
    ? {
        rules: [
          ...DEFAULT_RULES,
          ...policyOrExtra.map((pattern) => ({ pattern, mode: 'protected' as const })),
        ],
      }
    : policyOrExtra;
  const c = checkSite(url, policy);
  return c.blocked ? c.reason : null;
}
